import { useAuth } from '@contexts/auth';
import { useMemo } from 'react';
import { SubscriptionBanner } from '@components/SubscriptionBanner';
import { ProdDisclaimer } from '@page-components/prodDisclaimer';
import { CommonConstant, SubscriptionMessage } from '@utility/constants/common';
import { BrowserUtility } from '@utility/browser-utility';
import { config } from '@utility/config';
import { PaymentDeclinedWarning } from '@page-components/payment-declined-warning/PaymentDeclineWarning';
import { cn } from '@skupreme/shared/ui';

interface SecureLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  padding?: number;
  customClass?: string;
}

function SecureLayout({
  padding,
  customClass,
  className,
  style,
  children,
  ...rest
}: SecureLayoutProps) {
  const { subscription, user, footerActive } = useAuth();
  const showSubscription = useMemo(
    () =>
      subscription &&
      user?.role !== 'superadmin' &&
      subscription?.cancel_at_period_end &&
      (subscription?.expirationDays || Math.max()) < 10 &&
      window.location.pathname !== '/app/onboarding/',
    [subscription, user],
  );

  return (
    <>
      {BrowserUtility.get(CommonConstant.useProd) === 'true' &&
        config.env === 'STAGING' && <ProdDisclaimer />}
      {/* The scrolling part of the card; the card itself is `ContentCard`.
          The id is what AppTable scrolls back to the top. A `className`
          replaces `customClass`, as it always has. */}
      <div
        {...rest}
        className={cn(
          'sk:min-h-0 sk:flex-1 sk:overflow-auto',
          className ?? customClass,
        )}
        style={padding ? { padding, ...style } : style}
        id="main-skupreme-content"
      >
        {(user?.payment_failures?.count ?? 0) > 0 ||
          (['past_due'].includes(subscription?.status ?? '') && (
            <PaymentDeclinedWarning
              failureMessage={
                ['past_due'].includes(subscription?.status ?? '')
                  ? SubscriptionMessage?.past_due
                  : user?.payment_failures?.message
              }
            />
          ))}
        {children}
        {/* Room to scroll the last rows out from under the fixed footer bar. */}
        {footerActive && <div aria-hidden className="sk:h-[75px]" />}
      </div>

      {showSubscription && (
        <SubscriptionBanner
          subscription={subscription}
          viewPlan={console.log}
        />
      )}
    </>
  );
}

export default SecureLayout;
